import { Component, DestroyRef, Input, OnInit, inject } from '@angular/core';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbActiveModal, NgbNavModule } from '@ng-bootstrap/ng-bootstrap';
import { finalize } from 'rxjs/operators';
import { ItemsService } from '../items.service';
import { ItemRequestModel, SupplierInsertRequestModel } from '../models/item-request-model';
import { ErrorParserService } from '../../common-services/error-parser.service';
import { AllowedSupplierService } from '../../allowed-supplier/allowed-supplier.service';
import { AllowedSupplierResponseModel } from '../../allowed-supplier/models/allowed-supplier-response-model';
import { ItemSearchResponseModel } from '../models/item-search-response-model';
import { EnGbTextPipe } from '../../utils/en-gb-text.pipe';

type Controls<T> = { [Key in keyof T]: FormControl<T[Key]> };
type TextKey<T> = { [Key in keyof T]: T[Key] extends string | null ? Key : never }[keyof T];
type TextField<T> = { key: TextKey<T>; label: string; required?: boolean };
type SupplierForm = FormGroup<Controls<SupplierInsertRequestModel>>;
const ISO_8601_DURATION_PATTERN = /^(?:\d+(?:\.\d+)?|P(?=[\dT])(?:(?:\d+(?:\.\d+)?Y)?(?:\d+(?:\.\d+)?M)?(?:\d+(?:\.\d+)?D)?(?:T(?=\d)(?:\d+(?:\.\d+)?H)?(?:\d+(?:\.\d+)?M)?(?:\d+(?:\.\d+)?S)?)?|\d+(?:\.\d+)?W))$/;

@Component({
  selector: 'add-item',
  standalone: true,
  imports: [ReactiveFormsModule, NgbNavModule],
  providers: [ItemsService, AllowedSupplierService, EnGbTextPipe],
  templateUrl: './add-item.component.html'
})
export class AddItemComponent implements OnInit {
  @Input() public itemModel?: ItemSearchResponseModel;
  private readonly itemsService = inject(ItemsService);
  private readonly allowedSupplierService = inject(AllowedSupplierService);
  private readonly errorParser = inject(ErrorParserService);
  private readonly enGbText = inject(EnGbTextPipe);
  private readonly destroyRef = inject(DestroyRef);
  public readonly activeModal = inject(NgbActiveModal);
  public saving = false;
  public loading = false;
  public loadFailed = false;
  public errorMessage = '';
  public activeSupplierIndex = 0;
  public allowedSuppliers: AllowedSupplierResponseModel[] = [];
  public readonly suppliers = new FormArray<SupplierForm>([]);
  public readonly form = new FormGroup({
    itemId: this.requiredText(),
    description: this.requiredText(),
    detailedDescription: this.requiredText(),
    alsisProductCode: this.requiredText(),
    alsisScope: this.requiredText(),
    itemClassification: this.optionalText(),
    replacedById: this.optionalText(),
    replacedByReason: this.optionalText(),
    isConfigured: new FormControl(false, { nonNullable: true }),
    publishingSource: new FormControl('MANUAL_IMPORT', { nonNullable: true }),
    originSource: this.requiredText('MANUAL'),
    configuratorOrigin: this.optionalText(),
    taxonomyNodeId: this.optionalText(),
    taxonomyNodeName: this.optionalText(),
    pplCurrency: this.optionalText(),
    ppl: new FormControl<number | null>(null),
    supplierInsertRequestModel: this.suppliers
  });

  public readonly itemFields: TextField<ItemRequestModel>[] = [
    { key: 'itemId', label: 'Item ID', required: true },
    { key: 'description', label: 'Description', required: true },
    { key: 'detailedDescription', label: 'Detailed description', required: true },
    { key: 'alsisProductCode', label: 'Alsis product code', required: true },
    { key: 'alsisScope', label: 'Alsis scope', required: true },
    { key: 'originSource', label: 'Origin source', required: true },
    { key: 'itemClassification', label: 'Item classification' },
    { key: 'replacedById', label: 'Replaced by ID' },
    { key: 'replacedByReason', label: 'Replacement reason' },
    { key: 'configuratorOrigin', label: 'Configurator origin' },
    { key: 'taxonomyNodeId', label: 'Taxonomy node ID' },
    { key: 'taxonomyNodeName', label: 'Taxonomy node name' },
    { key: 'pplCurrency', label: 'PPL currency' }
  ];

  public readonly supplierFields: TextField<SupplierInsertRequestModel>[] = [
    { key: 'CountryOfOriginCode', label: 'Country of origin code', required: true },
    { key: 'leadTimeDuration', label: 'Lead time duration', required: true },
    { key: 'baseUOMCode', label: 'Base UOM code', required: true },
    { key: 'hazardousClass', label: 'Hazardous class' },
    { key: 'legalEntityId', label: 'Legal entity ID' },
    { key: 'siteId', label: 'Site ID' },
    { key: 'netWeightUoM', label: 'Net weight UOM' },
    { key: 'transferPriceCurrency', label: 'Transfer price currency' },
    { key: 'factoryCostCurrency', label: 'Factory cost currency' },
    { key: 'customsStatisticalCode', label: 'Customs statistical code' }
  ];

  public readonly itemClassificationOptions = [
    { value: '10', text: '10 - PRODUCT' },
    { value: '15', text: '15 - CONFIGURED_PRODUCT' },
    { value: '20', text: '20 - SERVICEKIT' },
    { value: '25', text: '25 - TOOL' },
    { value: '30', text: '30 - SPAREPART' },
    { value: '40', text: '40 - ACESSORY' },
    { value: '50', text: '50 - WEAR_PART' },
    { value: '60', text: '60 - SERVICE' },
    { value: '63', text: '63 - DIGITAL_SERVICE' },
    { value: '70', text: '70 - DTO' },
    { value: '80', text: '80 - PLATE' },
    { value: '99', text: '99 - NA' },
    { value: '999', text: '999 - OTHER' }
  ];

  public readonly hazardousClassOptions = [
    { value: '1', text: 'Class 1: Explosives' },
    { value: '2', text: 'Class 2: Gases' },
    { value: '3', text: 'Class 3: Flammable Liquids' },
    { value: '4', text: 'Class 4: Flammable Solids' },
    { value: '5', text: 'Class 5: Oxidizers and Organic Peroxides' },
    { value: '6', text: 'Class 6: Toxic and Infectious Substances' },
    { value: '7', text: 'Class 7: Radioactive Material' },
    { value: '8', text: 'Class 8: Corrosive Substances' },
    { value: '9', text: 'Class 9: Miscellaneous Dangerous Goods' }
  ];

  public readonly stockCodeOptions = [
    { value: 'NI', text: 'NI (Not in Stock)' },
    { value: 'BI', text: 'BI (Business Item)' },
    { value: 'RI', text: 'RI (Request Item)' },
    { value: 'SI', text: 'SI (Stocked Item)' },
    { value: 'NA', text: 'NA (Not Available)' }
  ];

  public readonly supplierNumberFields = [
    { key: 'netWeight', label: 'Net weight', required: true },
    { key: 'transferPrice', label: 'Transfer price', required: false },
    { key: 'factoryCost', label: 'Factory cost', required: false }
  ] as const;

  constructor() {
    this.allowedSupplierService.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(result => {
      this.allowedSuppliers = result.data as AllowedSupplierResponseModel[];
    });
    this.allowedSupplierService.query(
      { skip: 0, take: 100 },
      { logic: 'and', filters: [] }
    );
    this.addSupplier();
  }

  public get isEditing(): boolean {
    return !!this.itemModel;
  }

  public ngOnInit(): void {
    if (this.isEditing) this.loadItem();
  }

  public loadItem(): void {
    if (!this.itemModel || this.loading || this.saving) return;
    this.loading = true;
    this.loadFailed = false;
    this.errorMessage = '';
    this.itemsService.getItemv2(this.itemModel.itemId).pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => this.loading = false)
    ).subscribe({
      next: (item) => {
        const details = item.itemDetails;
        this.form.patchValue({
          itemId: details.itemId,
          description: this.enGbText.transform(details.description),
          detailedDescription: this.enGbText.transform(details.detailedDescription),
          alsisProductCode: details.alsisProductCode ?? '',
          alsisScope: details.alsisScope ?? '',
          itemClassification: item.classification?.itemClassification ?? null,
          isConfigured: details.isConfigured,
          publishingSource: item.origin?.publishingSource ?? this.itemModel!.publishingSource,
          originSource: item.origin?.originSource ?? '',
          configuratorOrigin: details.configuratorSource ?? null,
          taxonomyNodeId: item.classification?.taxonomyNodeId ?? null,
          taxonomyNodeName: item.classification?.taxonomyNodeName ?? null
        });
        this.form.controls.itemId.disable();
        this.suppliers.clear();
        let mainSupplierAssigned = false;
        for (const supplier of item.suppliers ?? []) {
          this.addSupplier();
          const supplierForm = this.suppliers.at(this.suppliers.length - 1);
          const supplierDetails = supplier.supplierDetails;
          const isMainSupplier: boolean = supplierDetails.isMainSupplier && !mainSupplierAssigned;
          mainSupplierAssigned ||= isMainSupplier;
          supplierForm.patchValue({
            supplierId: supplierDetails.supplierId,
            CountryOfOriginCode: supplierDetails.countryOfOriginCode ?? '',
            leadTimeDuration: supplier.leadtimes?.leadTimeDuration ?? '',
            baseUOMCode: supplierDetails.baseUOMCode ?? 'PCE',
            hazardousClass: supplier.hazardousMaterial?.hazardousClass ?? '',
            legalEntityId: supplierDetails.legalEntity ?? '',
            siteId: supplierDetails.siteId ?? '',
            netWeight: supplier.dimensions?.netWeight ?? 0,
            netWeightUoM: supplier.dimensions?.weightUoM ?? null,
            stockCode: supplierDetails.stockCode ?? '',
            customsStatisticalCode: supplierDetails.customsStatisticalCode ?? null,
            isMainSupplier
          });
          supplierForm.controls.supplierId.disable();
        }
        this.activeSupplierIndex = 0;
      },
      error: (error) => {
        this.loadFailed = true;
        this.errorMessage = this.errorParser.extractErrors(error).errorMessage ||
          'Failed to load the item. Please try again.';
      }
    });
  }

  public addSupplier(): void {
    if (this.saving) return;
    const isFirstSupplier = this.suppliers.length === 0;
    this.suppliers.push(new FormGroup({
      supplierId: this.requiredText(),
      CountryOfOriginCode: this.requiredText(),
      leadTimeDuration: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.pattern(/\S/), Validators.pattern(ISO_8601_DURATION_PATTERN)]
      }),
      baseUOMCode: new FormControl('PCE', {
        nonNullable: true,
        validators: [Validators.required, Validators.pattern(/\S/)]
      }),
      hazardousClass: new FormControl('', { nonNullable: true }),
      legalEntityId: this.optionalText(),
      siteId: this.optionalText(),
      netWeight: new FormControl(0, { nonNullable: true, validators: [Validators.required, Validators.min(Number.MIN_VALUE)] }),
      netWeightUoM: new FormControl<string | null>('kg'),
      transferPrice: new FormControl<number | null>(null),
      transferPriceCurrency: this.optionalText(),
      factoryCost: new FormControl<number | null>(null),
      factoryCostCurrency: this.optionalText(),
      stockCode: this.requiredText(),
      customsStatisticalCode: this.optionalText(),
      isMainSupplier: new FormControl(isFirstSupplier, { nonNullable: true })
    }));
    this.activeSupplierIndex = this.suppliers.length - 1;
  }

  public removeSupplier(index: number): void {
    if (this.saving || this.loading || this.loadFailed || this.suppliers.at(index).controls.supplierId.disabled) return;

    this.suppliers.removeAt(index);
    if (this.suppliers.length === 0) {
      this.activeSupplierIndex = 0;
    } else if (index < this.activeSupplierIndex) {
      this.activeSupplierIndex--;
    } else if (index === this.activeSupplierIndex) {
      this.activeSupplierIndex = Math.min(index, this.suppliers.length - 1);
    }
  }

  public getSupplierTabLabel(supplier: SupplierForm): string {
    return supplier.controls.supplierId.value.trim() || 'New supplier';
  }

  public isSupplierSelectedElsewhere(index: number, supplierId: string): boolean {
    return this.suppliers.controls.some((supplier, supplierIndex) =>
      supplierIndex !== index && supplier.controls.supplierId.value === supplierId
    );
  }

  public setMainSupplier(index: number, isMainSupplier: boolean): void {
    if (!isMainSupplier) return;

    this.suppliers.controls.forEach((supplier, supplierIndex) => {
      if (supplierIndex !== index && supplier.controls.isMainSupplier.value) {
        supplier.controls.isMainSupplier.setValue(false);
      }
    });
  }

  public normalizeLeadTimeDuration(supplier: SupplierForm): void {
    const control = supplier.controls.leadTimeDuration;
    const value = control.value.trim();
    if (/^\d+(?:\.\d+)?$/.test(value)) control.setValue(`P${value}D`);
  }

  public save(): void {
    if (this.saving || this.loading || this.loadFailed) return;
    for (const supplier of this.suppliers.controls) {
      this.normalizeLeadTimeDuration(supplier);
    }
    this.form.markAllAsTouched();
    if (this.form.invalid) return;

    for (const field of this.itemFields) {
      if (!field.required && this.form.controls[field.key].value === '') {
        this.form.controls[field.key].setValue(null);
      }
    }
    for (const supplier of this.suppliers.controls) {
      for (const field of this.supplierFields) {
        if (!field.required && field.key !== 'hazardousClass' && supplier.controls[field.key].value === '') {
          supplier.controls[field.key].setValue(null);
        }
      }
    }

    const request: ItemRequestModel = this.form.getRawValue();
    this.saving = true;
    this.errorMessage = '';
    const saveRequest = this.isEditing
      ? this.itemsService.updateItemFull(request)
      : this.itemsService.createItem(request);
    saveRequest.pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => this.saving = false)
    ).subscribe({
      next: () => this.activeModal.close({ action: 'save', itemId: request.itemId }),
      error: (error) => {
        this.errorMessage = this.errorParser.extractErrors(error).errorMessage ||
          (this.isEditing ? 'Failed to update the item. Please try again.' : 'Failed to add the item. Please try again.');
      }
    });
  }

  public cancel(): void {
    if (!this.saving) this.activeModal.dismiss('cancel');
  }

  private requiredText(initialValue = ''): FormControl<string> {
    return new FormControl(initialValue, {
      nonNullable: true,
      validators: [Validators.required, Validators.pattern(/\S/)]
    });
  }

  private optionalText(): FormControl<string | null> {
    return new FormControl<string | null>(null);
  }
}